import React, { useState, useEffect } from 'react';
import { Save, X, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { productService } from '../../services/productService';
import { notificationService } from '../../services/notificationService';
import { Product } from '../../types/product';
import { uploadImage, CloudinaryUploadResult } from '../../services/cloudinaryService';

interface ProductFormProps {
  product?: Product | null;
  onSave: () => void;
  onCancel: () => void;
}

const ProductForm = ({ product, onSave, onCancel }: ProductFormProps) => {
  const [formData, setFormData] = useState({
    name: '',
    category: 'spares',
    price: 0,
    description: '',
    image: '',
    rating: 5,
    reviews: 0,
    inStock: true,
    stock: 0,
    featured: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const categories = [
    { id: 'spares', name: 'Repuestos' },
    { id: 'machinery', name: 'Maquinaria' },
    { id: 'tools', name: 'Herramientas' },
    { id: 'safety', name: 'Seguridad' },
    { id: 'automation', name: 'Automatización' }
  ];

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        category: product.category,
        price: product.price,
        description: product.description,
        image: product.image,
        rating: product.rating,
        reviews: product.reviews,
        inStock: product.inStock,
        stock: product.stock,
        featured: product.featured
      });
    }
  }, [product]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre del producto es obligatorio';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'La descripción es obligatoria';
    }

    if (formData.price <= 0) {
      newErrors.price = 'El precio debe ser mayor a 0';
    }

    if (!formData.image.trim()) {
      newErrors.image = 'La imagen del producto es obligatoria';
    }

    if (formData.rating < 0 || formData.rating > 5) {
      newErrors.rating = 'La calificación debe estar entre 0 y 5';
    }

    if (formData.reviews < 0) {
      newErrors.reviews = 'El número de reseñas no puede ser negativo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      let success = false;
      if (product) {
        // Editar producto existente
        const updatedProduct = await productService.updateProduct(product.id, formData);
        success = updatedProduct !== null;
      } else {
        // Agregar nuevo producto
        const newProduct = await productService.addProduct(formData);
        success = newProduct !== null;
      }

      if (success) {
        notificationService.success('Producto guardado exitosamente', product ? 'El producto ha sido actualizado correctamente.' : 'El producto ha sido creado correctamente.');
        onSave();
      } else {
        notificationService.error('Error al guardar el producto', 'Por favor, verifique los datos e intente nuevamente.');
      }
    } catch (error) {
      console.error('Error al guardar el producto:', error);
      notificationService.error('Error al guardar el producto', 'Ha ocurrido un error inesperado. Por favor, intente nuevamente.');
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      notificationService.warning('Archivo no válido', 'Por favor, seleccione un archivo de imagen válido (JPG, PNG, GIF).');
      return;
    }

    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      notificationService.warning('Archivo demasiado grande', 'La imagen no debe superar los 5MB.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const result: CloudinaryUploadResult = await uploadImage(file, {
        folder: 'atomic-corporation/products',
        quality: 'auto',
        crop: 'limit',
        width: 1200,
        height: 1200
      });

      setFormData(prev => ({
        ...prev,
        image: result.secure_url
      }));

      // Limpiar error de imagen si existía
      if (errors.image) {
        setErrors(prev => ({ ...prev, image: '' }));
      }
    } catch (error) {
      console.error('Error al subir la imagen:', error);
      notificationService.error('Error al subir la imagen', 'Ha ocurrido un error al subir la imagen. Por favor, intente nuevamente.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleImageUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, image: value }));
    
    // Limpiar error cuando el usuario empieza a escribir
    if (errors.image) {
      setErrors(prev => ({ ...prev, image: '' }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : 
              type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));

    // Limpiar error cuando el usuario empieza a escribir
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-3 sm:p-4 lg:p-6">
      <div className="flex justify-between items-center mb-4 sm:mb-6">
        <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 truncate">
          {product ? 'Editar Producto' : 'Agregar Nuevo Producto'}
        </h3>
        <Button variant="outline" size="sm" onClick={onCancel} className="flex-shrink-0">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
          {/* Nombre del producto */}
          <div className="sm:col-span-2 lg:col-span-1">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Nombre del Producto *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Ej: Rodamiento Industrial SKF 6305"
            />
            {errors.name && (
              <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Categoría */}
          <div className="sm:col-span-2 lg:col-span-1">
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Categoría *
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base"
            >
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Precio */}
          <div>
            <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Precio (USD) *
            </label>
            <input
              type="number"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base ${
                errors.price ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="0.00"
            />
            {errors.price && (
              <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.price}</p>
            )}
          </div>

          {/* Rating */}
          <div>
            <label htmlFor="rating" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Calificación (0-5)
            </label>
            <input
              type="number"
              id="rating"
              name="rating"
              value={formData.rating}
              onChange={handleInputChange}
              step="0.1"
              min="0"
              max="5"
              className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base ${
                errors.rating ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="5.0"
            />
            {errors.rating && (
              <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.rating}</p>
            )}
          </div>

          {/* Reseñas */}
          <div>
            <label htmlFor="reviews" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Número de Reseñas
            </label>
            <input
              type="number"
              id="reviews"
              name="reviews"
              value={formData.reviews}
              onChange={handleInputChange}
              min="0"
              className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base ${
                errors.reviews ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="0"
            />
            {errors.reviews && (
              <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.reviews}</p>
            )}
          </div>

          {/* Stock */}
          <div>
            <label htmlFor="stock" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Cantidad en Stock
            </label>
            <input
              type="number"
              id="stock"
              name="stock"
              value={formData.stock}
              onChange={handleInputChange}
              min="0"
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base"
              placeholder="0"
            />
          </div>

          {/* Subida de imagen */}
          <div className="sm:col-span-2 lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Imagen del Producto *
            </label>
            
            <div className="space-y-3 sm:space-y-4">
              {/* Opción 1: Subir archivo */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1 sm:mb-2">
                  Subir desde tu dispositivo
                </label>
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                  <label className="cursor-pointer w-full sm:w-auto">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={isUploading}
                      className="hidden"
                    />
                    <div className={`flex items-center justify-center px-3 sm:px-4 py-2 sm:py-2.5 border-2 border-dashed rounded-lg transition-colors w-full sm:w-auto ${
                      isUploading
                        ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                        : 'border-primary-300 hover:border-primary-500 bg-primary-50 cursor-pointer'
                    }`}>
                      {isUploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          <span className="text-sm">Subiendo...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          <span className="text-sm">Seleccionar imagen</span>
                        </>
                      )}
                    </div>
                  </label>
                  
                  {isUploading && (
                    <div className="w-full sm:flex-1">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Formatos: JPG, PNG, GIF. Máximo 5MB
                </p>
              </div>

              {/* Opción 2: URL externa */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1 sm:mb-2">
                  O ingresa una URL externa
                </label>
                <input
                  type="url"
                  value={formData.image}
                  onChange={handleImageUrlChange}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base ${
                    errors.image ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="https://ejemplo.com/imagen.jpg"
                />
              </div>
            </div>
            
            {errors.image && (
              <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.image}</p>
            )}
          </div>
        </div>

        {/* Descripción */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
            Descripción *
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={3}
            className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base resize-none ${
              errors.description ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Describe el producto detalladamente..."
          />
          {errors.description && (
            <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.description}</p>
          )}
        </div>

        {/* Checkboxes */}
        <div className="flex flex-col sm:flex-row sm:space-x-6 space-y-2 sm:space-y-0">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="inStock"
              checked={formData.inStock}
              onChange={handleInputChange}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-2 w-4 h-4"
            />
            <span className="text-sm text-gray-700">En stock</span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              name="featured"
              checked={formData.featured}
              onChange={handleInputChange}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-2 w-4 h-4"
            />
            <span className="text-sm text-gray-700">Producto destacado</span>
          </label>
        </div>

        {/* Vista previa de la imagen */}
        {formData.image && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Vista previa de la imagen
            </label>
            <div className="flex flex-col sm:flex-row sm:items-start space-y-2 sm:space-y-0 sm:space-x-4">
              <div className="w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 border-2 border-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                <img
                  src={formData.image}
                  alt="Vista previa"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'https://via.placeholder.com/150x150.png?text=Error';
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm text-gray-600 truncate" title={formData.image}>
                  {formData.image}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.image.includes('cloudinary') ? 'Imagen almacenada en Cloudinary' : 'Imagen externa'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Botones */}
        <div className="flex flex-col sm:flex-row sm:justify-end sm:space-x-4 space-y-2 sm:space-y-0 pt-4 sm:pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="w-full sm:w-auto py-2 sm:py-2.5 text-sm sm:text-base"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            className="btn-primary w-full sm:w-auto py-2 sm:py-2.5 text-sm sm:text-base"
          >
            <Save className="w-4 h-4 mr-2" />
            {product ? 'Actualizar Producto' : 'Guardar Producto'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;